
import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import CommentForm from './CommentForm';
import { useAuth } from '../../../../hooks/useAuth';
import ReportModal from '../../../../components/base/ReportModal';
import VerifiedCreatorBadge from '../../../../components/base/VerifiedCreatorBadge';

interface Comment {
  id: string;
  content: string;
  content_html?: string;
  author_id: string;
  post_id: string;
  parent_id?: string;
  upvotes: number;
  downvotes: number;
  created_at: string;
  user_profiles?: {
    username: string;
    avatar_url?: string;
    reputation: number;
    is_verified_creator?: boolean;
  } | null;
}

interface CommentCardProps {
  comment: Comment;
  onVote: () => void;
  level?: number;
}

export default function CommentCard({ comment, onVote, level = 0 }: CommentCardProps) {
  const { user } = useAuth();
  const [showReportModal, setShowReportModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [userVote, setUserVote] = useState<'up' | 'down' | null>(null);
  const [voting, setVoting] = useState(false);

  useEffect(() => {
    if (user) {
      checkUserVote();
    }
  }, [user, comment.id]);

  const checkUserVote = async () => {
    try {
      const { data } = await supabase
        .from('comment_votes')
        .select('vote_type')
        .eq('user_id', user?.id)
        .eq('comment_id', comment.id)
        .single();

      if (data) {
        setUserVote(data.vote_type);
      }
    } catch (error) {
      // No vote found
    }
  };

  const handleVote = async (voteType: 'up' | 'down') => {
    if (!user || voting) return;

    try {
      setVoting(true);
      
      const { data: existingVote } = await supabase
        .from('comment_votes')
        .select('vote_type')
        .eq('comment_id', comment.id)
        .eq('user_id', user.id)
        .single();

      if (existingVote) {
        if (existingVote.vote_type === voteType) {
          // Remove vote
          await supabase
            .from('comment_votes')
            .delete()
            .eq('comment_id', comment.id)
            .eq('user_id', user.id);
          setUserVote(null);
        } else {
          // Update vote
          await supabase
            .from('comment_votes')
            .update({ vote_type: voteType })
            .eq('comment_id', comment.id)
            .eq('user_id', user.id);
          setUserVote(voteType);
        }
      } else {
        // Create new vote
        await supabase
          .from('comment_votes')
          .insert({
            comment_id: comment.id,
            user_id: user.id,
            vote_type: voteType
          });
        setUserVote(voteType);
      }

      // Refresh vote counts
      onVote();
    } catch (error) {
      console.error('Error voting:', error);
    } finally {
      setVoting(false);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return date.toLocaleDateString();
  };

  const handleReplyAdded = () => {
    setShowReplyForm(false);
    onVote(); // Refresh comments
  };

  const netVotes = comment.upvotes - comment.downvotes;

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          {comment.user_profiles?.avatar_url ? (
            <img 
              src={comment.user_profiles.avatar_url} 
              alt={comment.user_profiles.username}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {comment.user_profiles?.username?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">
                {comment.user_profiles?.username || 'Unknown User'}
              </span>
              {comment.user_profiles?.is_verified_creator && (
                <VerifiedCreatorBadge size="xs" />
              )}
              {comment.user_profiles?.reputation !== undefined && (
                <span className="text-sm text-gray-500">
                  ({comment.user_profiles.reputation} pts)
                </span>
              )}
            </div>
            <span className="text-sm text-gray-500">
              {formatTimeAgo(comment.created_at)}
            </span>
          </div>
        </div>
        
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors"
          >
            <i className="ri-more-line text-sm"></i>
          </button>
          
          {showMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
              <button
                onClick={() => {
                  setShowReportModal(true);
                  setShowMenu(false);
                }}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
              >
                <i className="ri-flag-line mr-2"></i>
                Report Comment
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mb-4">
        {comment.content_html ? (
          <div 
            className="prose max-w-none text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: comment.content_html }} 
          />
        ) : (
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
            {comment.content}
          </p>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {/* Voting */}
          <div className="flex items-center space-x-1">
            <button
              onClick={() => handleVote('up')}
              disabled={!user || voting}
              className={`p-1 rounded transition-colors ${
                userVote === 'up'
                  ? 'bg-green-100 text-green-600'
                  : 'hover:bg-gray-200 text-gray-600'
              } ${!user ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <i className="ri-arrow-up-line text-sm"></i>
            </button>
            <span className={`text-sm font-medium min-w-[20px] text-center ${
              netVotes > 0 ? 'text-green-600' : netVotes < 0 ? 'text-red-600' : 'text-gray-700'
            }`}>
              {netVotes}
            </span>
            <button
              onClick={() => handleVote('down')}
              disabled={!user || voting}
              className={`p-1 rounded transition-colors ${
                userVote === 'down'
                  ? 'bg-red-100 text-red-600'
                  : 'hover:bg-gray-200 text-gray-600'
              } ${!user ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <i className="ri-arrow-down-line text-sm"></i>
            </button>
          </div>

          {/* Reply */}
          {user && level < 3 && (
            <button
              onClick={() => setShowReplyForm(!showReplyForm)}
              className="text-sm text-gray-600 hover:text-indigo-600 transition-colors cursor-pointer"
            >
              <i className="ri-reply-line mr-1"></i>
              Reply
            </button>
          )}
        </div>
      </div>

      {showReplyForm && (
        <div className="mt-4">
          <CommentForm
            postId={comment.post_id}
            parentId={comment.id}
            onCommentAdded={handleReplyAdded}
            placeholder={`Reply to ${comment.user_profiles?.username || 'user'}...`}
          />
        </div>
      )}

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        contentType="comment"
        contentId={comment.id}
        reportedUserId={comment.author_id}
      />
    </div>
  );
}
